import type { ReactNode } from 'react';
import { Calendar, CalendarCheck, User } from 'lucide-react';
import { Card } from '@heroui/react';
import type { SpotUsage } from '../../../api/my-parking-spots';
import { UsageStatusChip, UsageTypeChip } from '../../../components/ui/usage-chips';
import {
  formatRequesterLabel,
  formatUsageDate,
  getActualEndAt,
  getActualStartAt,
} from '../../my-parking-spots/utils/usage-display';

interface UsageHistoryCardProps {
  usage: SpotUsage;
  highlighted?: boolean;
  showRequester?: boolean;
  showSpotCode?: boolean;
  hint?: string;
  actions?: ReactNode;
}

export function UsageHistoryCard({
  usage,
  highlighted = false,
  showRequester = false,
  showSpotCode = true,
  hint,
  actions,
}: UsageHistoryCardProps) {
  return (
    <Card className={highlighted ? 'ring-2 ring-accent' : undefined}>
      <Card.Content className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {showSpotCode ? (
            <span className="font-mono text-base font-semibold">{usage.spotCode}</span>
          ) : null}
          <UsageStatusChip status={usage.status} />
          <UsageTypeChip type={usage.type} />
        </div>

        {showRequester && (usage.userName || usage.userEmail) ? (
          <p className="flex items-center gap-1.5 text-sm text-muted">
            <User size={14} className="shrink-0" />
            <span>
              <strong className="text-foreground">Solicitado por:</strong>{' '}
              {formatRequesterLabel(usage)}
            </span>
          </p>
        ) : null}

        <p className="flex items-center gap-1.5 text-sm text-muted">
          <Calendar size={14} className="shrink-0" />
          <span>
            <strong className="text-foreground">Início:</strong>{' '}
            {formatUsageDate(getActualStartAt(usage))}
          </span>
        </p>
        <p className="flex items-center gap-1.5 text-sm text-muted">
          <CalendarCheck size={14} className="shrink-0" />
          <span>
            <strong className="text-foreground">Fim:</strong>{' '}
            {formatUsageDate(getActualEndAt(usage))}
          </span>
        </p>

        {hint ? <p className="text-xs text-warning">{hint}</p> : null}

        {actions ? <div className="flex flex-wrap gap-2 pt-1">{actions}</div> : null}
      </Card.Content>
    </Card>
  );
}
